/* PDP showcase v2 (draft theme, 2026-09-28). See snippets/nh-pdp-showcase.liquid.
   One core + four styles, chosen by product.metafields.nh_pdp.showcase_style:
     burst  (default) the cut-out bursts out of the gallery, bounces, floats, drag-rotates +-25deg
     lying  the product drops onto a glossy blush floor in perspective and tips to rest
     spin   a real 360 turntable built from the gallery's own angle shots (sprite, cross-faded)
     orbit  feature chips orbit the product in 3D, then dock beside it
   Core: the hero packshot paints first (LCP); the cut-out and GSAP (cdnjs, lazy, these pages
   only) load at low priority; the style plays when the gallery is on screen. Any other slide
   (swipe, thumbnail, variant) hands back to the theme gallery. Scroll sinks the layer back.
   Reduced motion (or GSAP unavailable): each style's final frame, static. */
(() => {
  'use strict';
  const doc = document;
  const layer = doc.querySelector('[data-nhs]');
  const frame = layer && layer.closest('.product__main-photos');
  const show = frame && frame.querySelector('[data-product-photos]');
  if (!layer || !show || layer.dataset.nhsInit) return;
  layer.dataset.nhsInit = '1';

  const STYLES = ['burst', 'lying', 'spin', 'orbit'];
  const style = STYLES.includes(layer.dataset.nhsStyle) ? layer.dataset.nhsStyle : 'burst';
  layer.classList.add(`nhs--${style}`);
  let reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const small = window.matchMedia('(max-width: 768px)').matches;
  const anchorIdx = parseInt(layer.dataset.nhsAnchor, 10) || 0;
  const GSAP = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.13.0/';
  const q = (s) => layer.querySelector(s);
  const mk = (cls, parent, tag = 'div') => { const n = doc.createElement(tag); n.className = cls; if (parent) parent.appendChild(n); return n; };
  // the cut-out is drawn into <canvas>: canvas is never a Largest Contentful Paint candidate, so the
  // (bigger, later) showcase can never replace the hero packshot as the page's LCP element
  const img = q('.nhs__img');
  const paint = [img];
  const el = { glow: q('.nhs__glow'), ring: q('.nhs__ring'), shadow: q('.nhs__shadow'), scroll: q('.nhs__scroll'), burst: q('.nhs__burst'), bob: q('.nhs__bob'), tilt: q('.nhs__tilt'), spin: q('.nhs__spin'), fx: q('.nhs__fx'), sheen: q('.nhs__sheen') };
  const mark = (k) => { try { performance.mark('nhs:' + k); } catch (e) { /* no-op */ } };
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

  const slideAt = (i) => show.querySelector(`.product-main-slide[data-index="${i}"]`) || show.querySelectorAll('.product-main-slide')[i];
  const anchor = slideAt(anchorIdx);
  if (!anchor) return;
  anchor.classList.add('nhs-anchor');

  let fl = null;
  let played = false;
  let visible = false;
  let inView = true;
  let idle = [];
  let gsap = null;
  let dragging = false;
  let angle = 0; // drag angle (deg); meaning depends on the style
  let tiltY = 0;

  const selectedIdx = () => (fl ? fl.selectedIndex : anchorIdx);
  const loadScript = (src) => new Promise((res, rej) => {
    const s = doc.createElement('script');
    s.src = src; s.async = true; s.crossOrigin = 'anonymous';
    s.onload = res; s.onerror = rej;
    doc.head.appendChild(s);
  });
  const loadGsap = () => (window.gsap && window.ScrollTrigger
    ? Promise.resolve()
    : (window.gsap ? Promise.resolve() : loadScript(GSAP + 'gsap.min.js')).then(() => (window.ScrollTrigger ? null : loadScript(GSAP + 'ScrollTrigger.min.js'))));

  // ---- geometry: cover the selected gallery cell (out of flow, so no layout shift) ----
  const place = () => {
    const cell = (fl && fl.selectedElement) || anchor;
    const fr = frame.getBoundingClientRect();
    const cr = cell.getBoundingClientRect();
    if (!cr.width || !fr.width) return;
    layer.style.width = `${Math.round(cr.width)}px`;
    layer.style.left = `${Math.round(cr.left - fr.left)}px`;
    layer.style.top = `${Math.round(cr.top - fr.top)}px`;
  };

  // ---- show / hide (hand-off with the theme gallery) ----
  const setIdle = (on) => { for (const t of idle) on ? t.resume() : t.pause(); if (S.idleToggle) S.idleToggle(on); };
  const setVisible = (on) => {
    if (on === visible) return;
    visible = on;
    if (on) place();
    frame.classList.toggle('nhs-on', on);
    layer.classList.toggle('is-shown', on);
    setIdle(on && inView);
  };
  let burstStarted = false;
  const play = () => {
    if (burstStarted) return;
    burstStarted = true;
    played = true;
    mark('burst');
    layer.dataset.nhsBurst = String(Math.round(performance.now()));
    place();
    if (!gsap || reduced) { S.final(); setVisible(true); return; }
    const tl = S.enter();
    tl.eventCallback('onComplete', () => {
      mark('idle');
      idle = S.idle() || [];
      setIdle(visible && inView);
      initScroll();
    });
  };
  const sync = () => {
    const on = selectedIdx() === anchorIdx;
    if (on && !played) { if (inView) play(); return; }
    setVisible(on && played);
  };

  // ---- shared bits ----
  const P_HEART = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 2.8 4.5 6.6 4.2c2.2-.2 4 1 5.4 2.9 1.4-1.9 3.2-3.1 5.4-2.9 3.8.3 5.7 4.2 4.2 7.6C19.5 16.4 12 21 12 21z"/></svg>';
  const P_SPARK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 0c.7 6.2 2.8 9.9 12 12-9.2 2.1-11.3 5.8-12 12-.7-6.2-2.8-9.9-12-12C9.2 9.9 11.3 6.2 12 0z"/></svg>';
  const COLORS = ['#e0103a', '#ff2d7a', '#ff6fa3', '#c2002f', '#ffb3cc'];
  const particles = (n = small ? 8 : 12, oy = 0.52) => {
    const size = layer.offsetWidth;
    for (let i = 0; i < n; i++) {
      const p = mk('nhs__p', el.fx, 'span');
      p.style.top = `${oy * 100}%`;
      p.innerHTML = i % 3 === 0 ? P_SPARK : P_HEART;
      p.style.color = COLORS[i % COLORS.length];
      const a = (i / n) * Math.PI * 2 + gsap.utils.random(-0.25, 0.25);
      const d = size * gsap.utils.random(0.36, 0.52);
      const s = gsap.utils.random(0.7, i % 3 === 0 ? 1.5 : 1.15);
      gsap.timeline({ onComplete: () => p.remove() })
        .fromTo(p, { x: 0, y: 0, scale: 0.2, rotation: gsap.utils.random(-40, 40), autoAlpha: 1 },
          { x: Math.cos(a) * d, y: Math.sin(a) * d * 0.85 - size * 0.04, scale: s, rotation: '+=' + gsap.utils.random(-90, 90), duration: gsap.utils.random(0.8, 1.1), ease: 'power3.out' })
        .to(p, { autoAlpha: 0, scale: s * 0.6, duration: 0.45, ease: 'power1.in' }, '-=0.45');
    }
  };
  const sheenAt = (v) => layer.style.setProperty('--nhs-hx', `${v}%`);

  /* =====================================================================
     STYLE A: burst (unchanged behaviour from v1)
     ===================================================================== */
  const burstStyle = {
    enter() {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      gsap.set(el.burst, { transformPerspective: 900, force3D: true });
      gsap.set([el.glow, el.shadow], { autoAlpha: 0 });
      tl.addLabel('go', 0).addLabel('land', 0.62)
        .fromTo(el.burst, { z: -650, scale: 0.34, rotationY: -22, yPercent: 7, autoAlpha: 0 },
          { z: 0, scale: small ? 1.2 : 1.16, rotationY: 6, yPercent: -5, autoAlpha: 1, duration: 0.62, ease: 'power4.out' }, 'go')
        .call(() => setVisible(true), null, 'go')
        .to(el.burst, { scale: 1, rotationY: 0, yPercent: 0, duration: 0.9, ease: 'elastic.out(1, 0.55)' }, 'land')
        .fromTo(el.shadow, { scaleX: 0.2, scaleY: 0.5, autoAlpha: 0 }, { scaleX: 1.35, scaleY: 1.3, autoAlpha: 0.35, duration: 0.62 }, 'go')
        .to(el.shadow, { scaleX: 1, scaleY: 1, autoAlpha: 1, duration: 0.7, ease: 'elastic.out(1, 0.6)' }, 'land')
        .fromTo(el.glow, { scale: 0.45, autoAlpha: 0 }, { scale: 1.12, autoAlpha: 1, duration: 0.5, ease: 'power2.out' }, 'land-=0.14')
        .to(el.glow, { scale: 1, duration: 0.7, ease: 'sine.inOut' }, '>')
        .call(() => particles(), null, 'land-=0.06')
        .fromTo(layer, { '--nhs-hx': '100%' }, { '--nhs-hx': '0%', duration: 1.0, ease: 'power2.inOut' }, 'land-=0.2');
      return tl;
    },
    idle() {
      gsap.set(el.burst, { clearProps: 'willChange' });
      return [
        gsap.to(el.bob, { y: small ? -6 : -9, duration: 1.9, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
        gsap.to(el.shadow, { scaleX: 0.86, autoAlpha: 0.72, duration: 1.9, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
        gsap.to(el.ring, { scale: 1.07, autoAlpha: 0.55, duration: 1.4, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
      ];
    },
    final() {},
    drag: { limit: 25, k: 0.34, flick: true },
    apply() {
      gsap.set(el.spin, { rotationY: angle, force3D: true });
      sheenAt(50 - (angle + tiltY) * 1.6);
    },
    tiltTarget: () => el.tilt,
  };

  /* =====================================================================
     STYLE B: lying. The product drops onto a glossy blush floor seen in
     perspective (rotateX 64deg), tips to rest, casts a contact shadow and
     a reflection. Pointer / drag tilts the whole stage.
     ===================================================================== */
  const REST = 0.635; // product base line, as a fraction of the square (cut-out is centred, 27% tall)
  const lyingStyle = (() => {
    let stage; let prod; let refl; let floor; let shadow;
    const pose = { y: 0, r: 0, x: 0 };
    const draw = () => {
      prod.style.transform = `translate3d(${pose.x}%, ${pose.y}%, 0) rotate(${pose.r}deg)`;
      refl.style.transform = `scaleY(-1) translate3d(${pose.x}%, ${pose.y}%, 0) rotate(${pose.r}deg)`;
      const lift = clamp(-pose.y / 40, 0, 1);
      shadow.style.opacity = String(1 - lift * 0.85);
      shadow.style.transform = `translateX(${pose.x}%) scale(${(1 - lift * 0.45) * 0.86}, ${1 - lift * 0.3})`;
      refl.style.opacity = String(0.2 * (1 - lift * 0.9));
    };
    return {
      build() {
        stage = mk('nhs__stage', null);
        const fw = mk('nhs__floorwrap', stage);
        floor = mk('nhs__floor', fw);
        this.fw = fw;
        shadow = mk('nhs__lshadow', stage);
        refl = mk('nhs__refl', stage);
        const rimg = doc.createElement('canvas'); rimg.className = 'nhs__img'; rimg.setAttribute('aria-hidden', 'true'); refl.appendChild(rimg); paint.push(rimg);
        prod = mk('nhs__prod', stage);
        prod.appendChild(img); prod.appendChild(el.sheen);
        el.burst.remove();
        el.scroll.appendChild(stage);
      },
      enter() {
        gsap.set(stage, { autoAlpha: 1 });
        const tl = gsap.timeline({ onUpdate: draw });
        tl.call(() => setVisible(true), null, 0)
          .fromTo(this.fw, { autoAlpha: 0, scaleX: 0.7 }, { autoAlpha: 1, scaleX: 1, duration: 0.6, ease: 'power2.out' }, 0)
          // drop, nose tilted, falling in from above the frame
          .fromTo(pose, { y: -72, r: -16, x: -4 }, { y: 0, r: 5, x: 0, duration: 0.62, ease: 'power2.in' }, 0.12)
          // tips back and rocks to rest on the floor
          .to(pose, { r: -2.2, y: -3.5, duration: 0.28, ease: 'power2.out' }, '>')
          .to(pose, { r: 0.8, y: 0, duration: 0.3, ease: 'power2.in' }, '>')
          .to(pose, { r: 0, duration: 0.7, ease: 'elastic.out(1, 0.5)' }, '>')
          .call(() => particles(small ? 6 : 9, REST - 0.02), null, 0.74)
          .fromTo(layer, { '--nhs-hx': '110%' }, { '--nhs-hx': '-10%', duration: 1.2, ease: 'power2.inOut' }, 0.9);
        return tl;
      },
      idle() {
        return [
          gsap.to(pose, { y: -2.4, duration: 2.2, ease: 'sine.inOut', yoyo: true, repeat: -1, onUpdate: draw }),
          gsap.fromTo(layer, { '--nhs-hx': '115%' }, { '--nhs-hx': '-15%', duration: 1.6, ease: 'power2.inOut', repeat: -1, repeatDelay: 3.2 }),
        ];
      },
      final() { pose.y = 0; pose.r = 0; pose.x = 0; draw(); },
      drag: { limit: 18, k: 0.3, flick: true },
      apply() {
        if (S.ry) S.ry(angle * 0.8 + tiltY); else gsap.set(stage, { rotationY: angle * 0.8 + tiltY, force3D: true });
        // the product rolls a little on the floor as the stage turns
        pose.x = angle * 0.18; pose.r = angle * 0.12; draw();
        sheenAt(50 - (angle + tiltY) * 2.2);
      },
      tiltTarget: () => stage,
    };
  })();

  /* =====================================================================
     STYLE C: spin. A true 360 turntable from the product's own gallery
     angle shots: an N-frame sprite (0..360deg), two faces cross-fading
     between neighbouring angles. Auto-rotates; drag spins with inertia.
     ===================================================================== */
  const spinStyle = (() => {
    const N = parseInt(layer.dataset.nhsFrames, 10) || 8;
    let turn; let faceA; let faceB; let platter; let hint;
    let yaw = 0; let vel = 0; let auto = 0; let lastInput = -1e9;
    const AUTO = 360 / 12; // deg per second
    const setFace = (face, i) => { face.style.backgroundPosition = `${(i / (N - 1)) * 100}% 0`; };
    const render = () => {
      const a = ((yaw % 360) + 360) % 360;
      const f = a / (360 / N);
      const i0 = Math.floor(f) % N; const i1 = (i0 + 1) % N;
      const t = f - Math.floor(f);
      // narrow cross-fade window: one clean angle most of the time, a quick dissolve between neighbours
      const u = clamp((t - 0.3) / 0.4, 0, 1);
      const s = u * u * (3 - 2 * u);
      if (faceA.dataset.i !== String(i0)) { setFace(faceA, i0); faceA.dataset.i = String(i0); }
      if (faceB.dataset.i !== String(i1)) { setFace(faceB, i1); faceB.dataset.i = String(i1); }
      faceB.style.opacity = s.toFixed(3);
      platter.firstChild.style.transform = `rotateX(74deg) rotateZ(${-yaw}deg)`;
    };
    const tick = (time, dt) => {
      if (!visible || !inView) return;
      const sec = Math.min(dt, 64) / 1000;
      if (!dragging) {
        yaw += vel;
        vel *= 0.94;
        if (Math.abs(vel) < 0.05) vel = 0;
        const want = performance.now() - lastInput > 1800 ? AUTO : 0;
        auto += (want - auto) * 0.04;
        yaw += auto * sec;
      }
      render();
    };
    return {
      build() {
        turn = mk('nhs__turn', null);
        faceA = mk('nhs__face', turn); faceB = mk('nhs__face', turn);
        platter = mk('nhs__platter', null);
        platter.innerHTML = '<svg viewBox="0 0 200 200" aria-hidden="true"><circle cx="100" cy="100" r="94" fill="none" stroke="currentColor" stroke-width="3"/><circle cx="100" cy="100" r="80" fill="none" stroke="currentColor" stroke-width="1" stroke-dasharray="2 9" opacity=".7"/><circle cx="100" cy="6" r="5" fill="currentColor"/></svg>';
        el.burst.remove();
        el.scroll.appendChild(platter);
        el.scroll.appendChild(turn);
        hint = mk('nhs__hint', layer, 'span');
        hint.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/><path d="M20.5 3.5v4.4h-4.4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Drag to spin</span>';
      },
      onSprite(url, w, h) {
        if (w && h) turn.style.aspectRatio = `${Math.round(w / N)} / ${h}`;
        turn.style.setProperty('--nhs-n', String(N));
        for (const f of [faceA, faceB]) f.style.backgroundImage = `url("${url}")`;
        render();
      },
      enter() {
        const tl = gsap.timeline();
        const spinIn = { v: -360 };
        tl.call(() => setVisible(true), null, 0)
          .fromTo(turn, { scale: 0.62, autoAlpha: 0, yPercent: 6 }, { scale: 1, autoAlpha: 1, yPercent: 0, duration: 0.9, ease: 'power3.out' }, 0)
          .fromTo(platter, { autoAlpha: 0, scale: 0.5 }, { autoAlpha: 1, scale: 1, duration: 0.8, ease: 'power3.out' }, 0.1)
          .fromTo(el.shadow, { autoAlpha: 0, scaleX: 0.4 }, { autoAlpha: 1, scaleX: 1, duration: 0.8 }, 0.2)
          // one confident full turn on arrival, easing into the slow auto-rotate
          .to(spinIn, { v: 0, duration: 1.7, ease: 'power3.out', onUpdate: () => { yaw = spinIn.v; render(); } }, 0)
          .fromTo(hint, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power3.out' }, 1.3);
        return tl;
      },
      idle() {
        lastInput = -1e9; auto = AUTO * 0.4;
        gsap.ticker.add(tick);
        return [];
      },
      idleToggle() {},
      final() { yaw = 0; render(); },
      // turntable: unbounded, no homing, no flick hand-off (a flick spins it)
      drag: { free: true },
      onDrag(step, v) { yaw += step * 0.6; vel = v * 0.6; lastInput = performance.now(); auto = 0; render(); if (hint && !hint.dataset.gone) { hint.dataset.gone = '1'; gsap.to(hint, { autoAlpha: 0, duration: 0.3 }); } },
      onRelease() { vel = clamp(vel, -14, 14); lastInput = performance.now(); },
      tiltTarget: () => null,
    };
  })();

  /* =====================================================================
     STYLE D: orbit. Feature chips (nh_pdp.showcase_chips, true facts only)
     orbit the product on a tilted ellipse, passing behind and in front,
     then dock beside it so the shopper can read them.
     ===================================================================== */
  const orbitStyle = (() => {
    let chips = []; let wrap;
    const st = { phase: 0, r: 0, dock: 0 };
    let slots = [];
    const N = () => chips.length;
    const layout = () => {
      const W = layer.offsetWidth || 300;
      const Rx = W * 0.43; const Ry = W * 0.13; const oy = W * 0.05;
      chips.forEach((c, i) => {
        const th = st.phase + (i / N()) * Math.PI * 2;
        const d = Math.sin(th);
        const ox = Math.cos(th) * Rx * st.r;
        const oyy = d * Ry * st.r + oy;
        const sl = slots[i];
        const x = ox + (sl.x * W - ox) * st.dock;
        const y = oyy + (sl.y * W - oyy) * st.dock;
        const depth = (d + 1) / 2;
        const sc = (0.74 + 0.26 * depth) * (1 - st.dock) + st.dock;
        const op = Math.min(1, st.r * 1.4) * ((0.45 + 0.55 * depth) * (1 - st.dock) + st.dock);
        c.style.transform = `translate3d(-50%, -50%, 0) translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${sc.toFixed(3)})`;
        c.style.opacity = op.toFixed(3);
        c.style.zIndex = st.dock > 0.5 || d > 0 ? '3' : '1';
      });
    };
    return {
      build() {
        let labels = [];
        try { labels = JSON.parse(layer.dataset.nhsChips || '[]'); } catch (e) { labels = []; }
        labels = labels.filter(Boolean).slice(0, 6);
        wrap = mk('nhs__chips', el.scroll);
        chips = labels.map((t) => { const c = mk('nhs__chip', wrap, 'span'); c.innerHTML = P_HEART; c.appendChild(doc.createTextNode(t)); return c; });
        // dock slots (fraction of the square, from its centre): alternate sides, top to bottom
        const L = labels.length;
        slots = labels.map((_, i) => {
          const side = i % 2 === 0 ? -1 : 1;
          const row = L > 1 ? i / (L - 1) : 0.5;
          return { x: side * (small ? 0.23 : 0.24), y: -0.34 + row * 0.7 };
        });
        layout();
      },
      enter() {
        const tl = gsap.timeline();
        gsap.set(el.burst, { transformPerspective: 900 });
        tl.call(() => setVisible(true), null, 0)
          .fromTo(el.burst, { scale: 0.72, yPercent: 8, autoAlpha: 0, rotationY: -14 }, { scale: 1, yPercent: 0, autoAlpha: 1, rotationY: 0, duration: 0.9, ease: 'power3.out' }, 0)
          .fromTo(el.glow, { scale: 0.5, autoAlpha: 0 }, { scale: 1, autoAlpha: 0.8, duration: 0.9, ease: 'power2.out' }, 0.1)
          .fromTo(el.shadow, { autoAlpha: 0, scaleX: 0.4 }, { autoAlpha: 1, scaleX: 1, duration: 0.8 }, 0.1)
          // chips fly out and orbit (~1.3 turns), slowing as they come round to dock
          .fromTo(st, { r: 0 }, { r: 1, duration: 0.8, ease: 'power3.out', onUpdate: layout }, 0.15)
          .fromTo(st, { phase: -Math.PI * 0.5 }, { phase: Math.PI * 2.1, duration: 2.9, ease: 'power1.inOut', onUpdate: layout }, 0.15)
          .to(st, { dock: 1, duration: 0.9, ease: 'power3.inOut', onUpdate: layout }, 2.4)
          .fromTo(layer, { '--nhs-hx': '100%' }, { '--nhs-hx': '0%', duration: 1.0, ease: 'power2.inOut' }, 0.5);
        return tl;
      },
      idle() {
        const t = [
          gsap.to(el.bob, { y: small ? -5 : -8, duration: 2.1, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
          gsap.to(el.shadow, { scaleX: 0.88, autoAlpha: 0.75, duration: 2.1, ease: 'sine.inOut', yoyo: true, repeat: -1 }),
        ];
        chips.forEach((c, i) => t.push(gsap.to(c, { '--nhs-cy': small ? '-3px' : '-4px', duration: 1.6 + i * 0.17, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: i * 0.2 })));
        return t;
      },
      final() { st.r = 1; st.dock = 1; layout(); if (gsap) gsap.set([el.burst, el.glow, el.shadow], { autoAlpha: 1 }); },
      drag: { limit: 25, k: 0.34, flick: true },
      apply() {
        gsap.set(el.spin, { rotationY: angle, force3D: true });
        sheenAt(50 - (angle + tiltY) * 1.6);
      },
      tiltTarget: () => el.tilt,
      onResize: () => layout(),
    };
  })();

  const S = { burst: burstStyle, lying: lyingStyle, spin: spinStyle, orbit: orbitStyle }[style];
  if (S.build) S.build();

  // ---- pointer: tilt (desktop), drag (all), tap = zoom ----
  const initPointer = () => {
    const hit = mk('nhs__hit', layer, 'span');
    const zoomBtn = anchor.querySelector('.js-photoswipe__zoom');
    const zoom = () => { if (zoomBtn) zoomBtn.click(); };
    const zr = zoomBtn && zoomBtn.getBoundingClientRect();
    if (zoomBtn && zr.width && zr.width < anchor.offsetWidth * 0.4) {
      // phones: mirror the small zoom button above the layer (desktop's zoom is a full overlay; the hit area zooms)
      const b = zoomBtn.cloneNode(true);
      b.classList.remove('js-photoswipe__zoom');
      b.setAttribute('aria-hidden', 'true');
      b.tabIndex = -1;
      b.addEventListener('click', (e) => { e.preventDefault(); zoom(); });
      layer.appendChild(b);
    }
    if (reduced || !gsap) { hit.addEventListener('click', zoom); return; }

    const D = S.drag;
    const tgt = S.tiltTarget();
    let rx = null; let ry = null;
    if (tgt) {
      gsap.set(tgt, { transformPerspective: 900 });
      rx = gsap.quickTo(tgt, 'rotationX', { duration: 0.5, ease: 'power3' });
      ry = gsap.quickTo(tgt, 'rotationY', { duration: 0.5, ease: 'power3' });
      if (style === 'lying') S.ry = gsap.quickTo(tgt, 'rotationY', { duration: 0.12, ease: 'power2' });
    }
    if (fine && tgt && style !== 'lying') {
      frame.addEventListener('pointermove', (e) => {
        if (!visible || dragging) return;
        const r = layer.getBoundingClientRect();
        const x = clamp((e.clientX - r.left) / r.width - 0.5, -0.5, 0.5);
        const y = clamp((e.clientY - r.top) / r.height - 0.5, -0.5, 0.5);
        tiltY = x * 16;
        ry(tiltY); rx(-y * 12);
        sheenAt(50 - (angle + tiltY) * 1.6);
      });
      frame.addEventListener('pointerleave', () => { tiltY = 0; rx(0); ry(0); });
    }
    if (fine && style === 'lying') {
      // the floor tilts toward the pointer: rotateY on the stage (via apply), a little rotateX
      frame.addEventListener('pointermove', (e) => {
        if (!visible || dragging) return;
        const r = layer.getBoundingClientRect();
        const x = clamp((e.clientX - r.left) / r.width - 0.5, -0.5, 0.5);
        const y = clamp((e.clientY - r.top) / r.height - 0.5, -0.5, 0.5);
        tiltY = x * 14;
        ry(angle * 0.8 + tiltY); rx(-y * 8);
        sheenAt(50 - (angle + tiltY) * 2.2);
      });
      frame.addEventListener('pointerleave', () => { tiltY = 0; ry(angle * 0.8); rx(0); });
    }

    // drag horizontally on the product; vertical pans still scroll the page (touch-action: pan-y)
    let axis = null; let sx = 0; let sy = 0; let lx = 0; let lt = 0; let vel = 0; let dx = 0; let raf = 0;
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const coast = () => {
      angle += vel;
      vel *= 0.92;
      if (Math.abs(angle) > D.limit) { angle = Math.sign(angle) * D.limit; vel *= -0.35; }
      if (Math.abs(vel) < 0.08) { angle += (0 - angle) * 0.045; vel = 0; }
      S.apply();
      raf = Math.abs(angle) > 0.05 || Math.abs(vel) > 0.01 ? requestAnimationFrame(coast) : 0;
      if (!raf) { angle = 0; S.apply(); }
    };
    hit.addEventListener('pointerdown', (e) => {
      if (!visible || (e.pointerType === 'mouse' && e.button !== 0)) return;
      stop();
      dragging = true; axis = null; dx = 0; vel = 0;
      sx = lx = e.clientX; sy = e.clientY; lt = e.timeStamp;
    });
    hit.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (!axis) {
        const ax = Math.abs(e.clientX - sx); const ay = Math.abs(e.clientY - sy);
        if (ax < 6 && ay < 6) return;
        axis = ax > ay ? 'x' : 'y';
        if (axis === 'y') { dragging = false; return; }
        try { hit.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
        layer.classList.add('is-dragging');
      }
      const step = e.clientX - lx;
      const dt = Math.max(e.timeStamp - lt, 1);
      lx = e.clientX; lt = e.timeStamp; dx = e.clientX - sx;
      if (D.free) { S.onDrag(step, step / (dt / 16.7)); return; }
      const k = Math.abs(angle) > D.limit ? 0.12 : D.k;
      angle = clamp(angle + step * k, -D.limit - 7, D.limit + 7);
      vel = (step * k) / (dt / 16.7);
      S.apply();
    });
    const end = (e) => {
      if (!dragging) return;
      dragging = false;
      layer.classList.remove('is-dragging');
      if (!axis) { if (e.type === 'pointerup') zoom(); return; }
      if (D.free) { S.onRelease(); return; }
      // a hard flick hands off to the gallery: next / previous image
      const fast = Math.abs(vel) > 3.2 && Math.abs(dx) > 60;
      if (fast && fl && D.flick) { vel = 0; stop(); angle = 0; S.apply(); fl[dx < 0 ? 'next' : 'previous'](true); return; }
      vel = clamp(vel, -6, 6);
      stop(); raf = requestAnimationFrame(coast);
    };
    hit.addEventListener('pointerup', end);
    hit.addEventListener('pointercancel', end);
    hit.addEventListener('lostpointercapture', (e) => { if (dragging) end(e); });
    hit.addEventListener('dragstart', (e) => e.preventDefault());
  };

  // ---- scroll: the product sinks back into the frame ----
  const initScroll = () => {
    const ST = window.ScrollTrigger;
    if (!ST) return;
    gsap.registerPlugin(ST);
    gsap.timeline({
      scrollTrigger: { trigger: frame, start: 0, end: () => `+=${Math.round(frame.offsetHeight * 1.1)}`, scrub: 0.5, invalidateOnRefresh: true },
    })
      .fromTo(el.scroll, { scale: 1, yPercent: 0 }, { scale: 0.78, yPercent: 5, ease: 'none' }, 0)
      .fromTo(el.glow, { '--nhs-g': 1 }, { '--nhs-g': 0, ease: 'none' }, 0)
      .fromTo(layer, { '--nhs-sheen': 0.55 }, { '--nhs-sheen': 0.2, ease: 'none' }, 0);
  };

  // ---- boot ----
  const whenFlickity = () => new Promise((res) => {
    const has = () => window.Flickity && window.Flickity.data && window.Flickity.data(show);
    if (has() || show.querySelectorAll('.product-main-slide').length < 2) { res(has() || null); return; }
    const mo = new MutationObserver(() => { if (has()) { mo.disconnect(); clearTimeout(t); res(has()); } });
    mo.observe(show, { attributes: true, attributeFilter: ['class'] });
    const t = setTimeout(() => { mo.disconnect(); res(has() || null); }, 3000);
  });
  const heroPainted = () => new Promise((res) => {
    // wait for the packshot on screen (the LCP) so the cut-out never competes with it
    const cell = show.querySelector('.product-main-slide.starting-slide, .product-main-slide.is-selected') || anchor;
    const im = cell.querySelector('img');
    const go = () => requestAnimationFrame(() => requestAnimationFrame(res));
    if (!im || im.complete) { go(); return; }
    im.addEventListener('load', go, { once: true });
    im.addEventListener('error', go, { once: true });
    setTimeout(go, 4000);
  });
  const cssReady = () => new Promise((res) => {
    const ok = () => getComputedStyle(layer).getPropertyValue('--nhs-ready').trim() === '1';
    if (ok()) { res(); return; }
    const link = doc.getElementById('nhs-css');
    const t = setTimeout(res, 5000);
    if (link) link.addEventListener('load', () => { clearTimeout(t); requestAnimationFrame(res); }, { once: true });
  });
  const idleCb = () => new Promise((res) => ('requestIdleCallback' in window ? requestIdleCallback(() => res(), { timeout: 600 }) : setTimeout(res, 150)));
  const decode = (im) => (im.decode ? im.decode() : Promise.resolve()).catch(() => null);
  const loadCutout = () => {
    const w = Math.round(Math.max(layer.offsetWidth, 320));
    const im = new Image();
    im.decoding = 'async';
    try { im.fetchPriority = 'low'; } catch (e) { /* older browsers */ }
    im.sizes = `${w}px`;
    im.srcset = layer.dataset.nhsSrcset;
    im.src = layer.dataset.nhsSrc;
    const jobs = [decode(im).then(() => {
      if (!im.naturalWidth) return;
      const px = Math.min(1000, Math.round(w * Math.min(window.devicePixelRatio || 1, 2.5)));
      for (const c of paint) {
        c.width = px; c.height = px;
        const ctx = c.getContext('2d');
        if (ctx) ctx.drawImage(im, 0, 0, px, px);
      }
      layer.style.setProperty('--nhs-mask', `url("${im.currentSrc || im.src}")`);
      layer.classList.add('has-mask');
    })];
    if (style === 'spin' && layer.dataset.nhsSprite) {
      const sp = new Image();
      sp.decoding = 'async';
      sp.src = layer.dataset.nhsSprite;
      jobs.push(decode(sp).then(() => S.onSprite(sp.src, sp.naturalWidth, sp.naturalHeight)));
    }
    return Promise.all(jobs);
  };

  const boot = async () => {
    fl = await whenFlickity();
    await heroPainted();
    await idleCb();
    mark('load-start');
    const tasks = [cssReady(), loadCutout()];
    if (!reduced) tasks.push(loadGsap().then(() => { gsap = window.gsap; }, () => { gsap = null; }));
    await Promise.all(tasks);
    if (!gsap) reduced = true; // no GSAP (blocked / offline): the static final frame, never a broken half-animation
    mark('ready');
    place();
    layer.classList.add('is-ready');
    if (reduced) layer.classList.add('is-static');
    initPointer();

    if (fl) {
      fl.on('select', sync);
      fl.on('settle', () => { if (visible) place(); sync(); });
      fl.on('dragStart', () => setVisible(false));
    }
    let rt = 0;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (visible) place(); if (S.onResize) S.onResize(); }, 150); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        inView = e.isIntersecting;
        setIdle(visible && inView);
        if (inView && !played && selectedIdx() === anchorIdx) play();
      }, { threshold: 0.35 }).observe(frame);
    } else {
      sync();
    }
  };

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
